<template>
    <div class="section text-center pt-3" v-if="loading">
        <div class="spinner-border text-secondary" role="status"></div>
    </div>

    <template v-else-if="application.id">
        <div class="section pt-1 mt-0">
            <div class="card sneha-header-card">
                <div class="card-body mal-text text-center">
                    <h3 class="sneha-header-title">{{ application.program_title }}</h3>
                    <span class="sneha-app-number" v-if="application.application_number">{{ application.application_number }}</span>
                    <span class="sneha-approver-score-badge" v-if="application.is_approver">
                        <ion-icon name="star-outline"></ion-icon> Approver Score: {{ application.approver_score_total || 0 }}/{{ application.approver_score_max || 0 }}
                    </span>

                    <div class="sneha-header-highlight" v-if="application.extra_fields && application.extra_fields.length">
                        <span class="sneha-top-field-label">{{ application.extra_fields[0].label }}</span>
                        <span class="sneha-top-field-value">{{ application.extra_fields[0].value || '—' }}</span>
                    </div>

                    <p class="sneha-header-date" v-if="application.submitted_at">
                        സമർപ്പിച്ചത്: {{ application.submitted_at }}
                    </p>
                </div>
            </div>
        </div>

        <!-- Verification status banner (visible to unit / conveners once VO acted). 'in_progress'
             is an autosaved draft, not a real decision yet, so it's excluded here too. -->
        <div class="section pt-1" v-if="application.verification && application.verification.status && application.verification.status !== 'pending' && application.verification.status !== 'in_progress'">
            <div class="card" :style="'border-left:4px solid ' + statusColor(application.verification.status) + ';'">
                <div class="card-body mal-text" style="padding:12px 14px;">
                    <div class="d-flex justify-content-between align-items-center">
                        <b :style="'color:' + statusColor(application.verification.status) + ';'">
                            {{ statusLabel(application.verification.status) }}
                        </b>
                        <small class="text-muted" v-if="application.verification.officer_name">
                            {{ application.verification.officer_name }}<span v-if="application.verification.verified_at"> · {{ application.verification.verified_at }}</span>
                        </small>
                    </div>
                    <p class="mb-0 mt-1 small" v-if="application.verification.notes">
                        <b>Note:</b> {{ application.verification.notes }}
                    </p>
                    <router-link
                        v-if="!application.is_vo && ['not_verified', 'edit_requested'].includes(application.verification.status)"
                        :to="editPath"
                        class="btn btn-sm btn-outline-primary mt-2" style="border-radius:8px;">
                        <ion-icon name="create-outline"></ion-icon> വിവരങ്ങൾ എഡിറ്റ് ചെയ്യുക
                    </router-link>
                </div>
            </div>
        </div>

        <div class="section pt-1">
            <div class="card">
                <div class="card-body mal-text">
                    <h6 class="text-primary mb-2">അപേക്ഷ വിവരങ്ങൾ</h6>
                    <div class="detail-row" v-if="application.added_by_name">
                        <span class="detail-label">ചേർത്തത്</span>
                        <span class="detail-value">{{ application.added_by_name }}</span>
                    </div>
                    <div class="detail-row">
                        <span class="detail-label">ജില്ല</span>
                        <span class="detail-value">{{ application.district || '—' }}</span>
                    </div>
                    <div class="detail-row">
                        <span class="detail-label">മണ്ഡലം</span>
                        <span class="detail-value">{{ application.zone || '—' }}</span>
                    </div>
                    <div class="detail-row">
                        <span class="detail-label">ശാഖ</span>
                        <span class="detail-value">{{ application.unit || '—' }}</span>
                    </div>
                </div>
            </div>
        </div>

        <div class="section pt-0" v-if="application.relief">
            <div class="card">
                <div class="card-body mal-text">
                    <h6 class="text-primary mb-2">കുടുംബ വിവരങ്ങൾ</h6>
                    <div class="detail-row">
                        <span class="detail-label">കുടുംബ നാഥന്റെ പേര്</span>
                        <span class="detail-value">{{ application.relief.family_head_name || '—' }}</span>
                    </div>
                    <div class="detail-row">
                        <span class="detail-label">ലിംഗം</span>
                        <span class="detail-value">{{ formatGender(application.relief.family_head_gender) }}</span>
                    </div>
                    <div class="detail-row">
                        <span class="detail-label">വയസ്സ്</span>
                        <span class="detail-value">{{ application.relief.family_head_age || '—' }}</span>
                    </div>
                    <div class="detail-row" v-if="application.relief.family_head_surname">
                        <span class="detail-label">വീട്ട് പേര്</span>
                        <span class="detail-value">{{ application.relief.family_head_surname }}</span>
                    </div>
                    <div class="detail-row">
                        <span class="detail-label">ഫോൺ</span>
                        <span class="detail-value">
                            <a v-if="application.relief.family_contact" :href="'tel:' + application.relief.family_contact">
                                {{ application.relief.family_contact }}
                            </a>
                            <template v-else>—</template>
                        </span>
                    </div>
                    <div class="detail-row" v-if="application.relief.family_place">
                        <span class="detail-label">താമസിക്കുന്ന സ്ഥലം</span>
                        <span class="detail-value">{{ application.relief.family_place }}</span>
                    </div>
                    <div class="detail-row" v-if="application.relief.family_panchayath">
                        <span class="detail-label">പഞ്ചായത്ത്</span>
                        <span class="detail-value">{{ application.relief.family_panchayath }}</span>
                    </div>
                    <div class="detail-row detail-row--stacked" v-if="application.relief.family_reason_to_include">
                        <span class="detail-label">ലിസ്റ്റിൽ ഉൾപ്പെടുത്താനുള്ള കാരണം</span>
                        <span class="detail-value">{{ application.relief.family_reason_to_include }}</span>
                    </div>
                    <div class="detail-row" v-if="application.relief.zakath_eligible">
                        <span class="detail-label">സകാത്ത് അർഹത</span>
                        <span class="detail-value">{{ formatYesNo(application.relief.zakath_eligible) }}</span>
                    </div>
                    <div class="detail-row">
                        <span class="detail-label">കുടുംബത്തിലെ ആകെ എണ്ണം</span>
                        <span class="detail-value">{{ application.relief.family_members_count || '—' }}</span>
                    </div>
                    <div class="detail-row">
                        <span class="detail-label">16 വയസ്സിൽ താഴെയുള്ള കുട്ടികൾ</span>
                        <span class="detail-value">{{ application.relief.boys_count || '0' }}</span>
                    </div>
                    <div class="detail-row">
                        <span class="detail-label">യാതീം എണ്ണം</span>
                        <span class="detail-value">{{ application.relief.orphan_count || '0' }}</span>
                    </div>
                    <div class="detail-row">
                        <span class="detail-label">വിധവ എണ്ണം</span>
                        <span class="detail-value">{{ application.relief.widow_count || '0' }}</span>
                    </div>
                    <div v-if="application.relief.children && application.relief.children.length" class="mt-2">
                        <span class="detail-label d-block mb-1">കുട്ടികൾ</span>
                        <div
                            v-for="(child, idx) in application.relief.children"
                            :key="idx"
                            class="child-chip"
                        >
                            {{ child.name }} ({{ formatGender(child.sex) }}, {{ child.age }})
                        </div>
                    </div>
                </div>
            </div>
        </div>

        <div class="section pt-0" v-if="application.extra_fields && application.extra_fields.length">
            <div class="card">
                <div class="card-body mal-text">
                    <h6 class="text-primary mb-2">പദ്ധതി അപേക്ഷ വിവരങ്ങൾ</h6>
                    <div class="detail-row detail-row--stacked" v-for="field in application.extra_fields" :key="field.key">
                        <span class="detail-label">{{ field.label }}</span>
                        <span class="detail-value">{{ field.value || '—' }}</span>
                        <span class="badge sneha-q-mark-badge" v-if="field.mark !== null && field.mark !== undefined">
                            <ion-icon name="star-outline"></ion-icon> Mark: {{ field.mark }}
                        </span>
                    </div>
                </div>
            </div>
        </div>

        <!-- ============ Verification details (read-only, for non-VO viewers e.g. reports) ============ -->
        <div class="section pt-0"
            v-if="!application.is_vo && application.verification && ['verified', 'not_verified', 'edit_requested', 'resubmitted'].includes(application.verification.status)">
            <div class="card vo-card">
                <div class="card-body mal-text">
                    <div class="vo-head">
                        <ion-icon name="shield-checkmark-outline"></ion-icon>
                        <span class="mal-text">Verification Officer പരിശോധിച്ച വിവരങ്ങൾ</span>
                    </div>

                    <div class="vo-item-wrap" v-for="item in verifyItems" :key="'ro-' + item.key">
                        <div class="vo-item" :class="{ 'vo-item--ticked': application.verification.field_ticks[item.key], 'vo-item--wrap': item.wrap }">
                            <div class="vo-item-info">
                                <div class="vo-item-label">{{ item.label }}</div>
                                <div class="vo-item-value">{{ item.value || '—' }}</div>
                            </div>
                            <div class="vo-item-actions">
                                <span class="vo-tick" :class="{ 'vo-tick--on': application.verification.field_ticks[item.key] }">
                                    <ion-icon :name="application.verification.field_ticks[item.key] ? 'checkmark-circle' : 'ellipse-outline'"></ion-icon>
                                    <span>Verified</span>
                                </span>
                            </div>
                        </div>
                        <p class="vo-note-readonly mal-text" v-if="application.verification.field_notes[item.key]">
                            {{ application.verification.field_notes[item.key] }}
                        </p>
                        <div class="sneha-score-input" v-if="application.is_approver && isScorableItem(item.key)">
                            <span class="sneha-score-label">Score</span>
                            <input type="number" min="0" max="10" class="sneha-score-field"
                                :class="{ 'sneha-score-field--error': scoreInvalid(scores[scoreFieldKey(item.key)]) }"
                                v-model.number="scores[scoreFieldKey(item.key)]">
                            <span class="sneha-score-max">/ 10</span>
                            <span class="sneha-score-error" v-if="scoreInvalid(scores[scoreFieldKey(item.key)])">0–10 മാത്രം</span>
                        </div>
                        <div class="sneha-comment-input" v-if="application.is_approver && isCommentableItem(item.key)">
                            <input type="text" class="sneha-comment-field mal-text" placeholder="അഭിപ്രായം (ഓപ്ഷണൽ)"
                                v-model="comments[scoreFieldKey(item.key)]">
                        </div>
                    </div>

                    <template v-if="(application.verification_questions || []).length">
                        <div class="vo-subhead mal-text">അധിക ചോദ്യങ്ങൾ</div>
                        <div class="detail-row detail-row--stacked" v-for="q in application.verification_questions" :key="'roq-' + q.key">
                            <span class="detail-label mal-text">{{ q.label }}</span>
                            <span class="detail-value mal-text">
                                {{ application.verification.extra_answers[q.key] || '—' }}
                                <template v-if="application.verification.extra_answers[q.key + '__extra']"> ({{ application.verification.extra_answers[q.key + '__extra'] }})</template>
                            </span>
                            <span class="badge sneha-q-mark-badge" v-if="answerMark(q) !== null">
                                <ion-icon name="star-outline"></ion-icon> Mark: {{ answerMark(q) }}
                            </span>
                            <div class="sneha-score-input" v-if="application.is_approver && (application.approver_scorable_keys || []).includes(q.key)">
                                <span class="sneha-score-label">Score</span>
                                <input type="number" min="0" max="10" class="sneha-score-field"
                                    :class="{ 'sneha-score-field--error': scoreInvalid(scores[q.key]) }"
                                    v-model.number="scores[q.key]">
                                <span class="sneha-score-max">/ 10</span>
                                <span class="sneha-score-error" v-if="scoreInvalid(scores[q.key])">0–10 മാത്രം</span>
                            </div>
                            <div class="sneha-comment-input" v-if="application.is_approver && (application.approver_commentable_keys || []).includes(q.key)">
                                <input type="text" class="sneha-comment-field mal-text" placeholder="അഭിപ്രായം (ഓപ്ഷണൽ)"
                                    v-model="comments[q.key]">
                            </div>
                        </div>
                    </template>

                    <div class="mt-2" v-if="application.verification.notes">
                        <label class="field-label mal-text">Note</label>
                        <p class="mb-0 mal-text" style="font-size:15px;color:#1e293b;">{{ application.verification.notes }}</p>
                    </div>

                    <p class="sneha-score-error mt-1" v-if="application.is_approver && hasInvalidScores">
                        ചില സ്കോറുകൾ 0–10 പരിധിക്ക് പുറത്താണ്. തിരുത്തിയ ശേഷം സേവ് ചെയ്യുക.
                    </p>
                    <button type="button" class="btn btn-primary sneha-save-score-btn" v-if="application.is_approver"
                        :disabled="savingScores || hasInvalidScores" @click="saveScores">
                        <span v-if="savingScores" class="spinner-border spinner-border-sm me-1"></span>
                        സ്കോർ സേവ് ചെയ്യുക
                    </button>
                </div>
            </div>
        </div>

        <!-- ============ Verification form (Verification Officer only) ============ -->
        <div class="section pt-0" v-if="application.is_vo">
            <div class="card vo-card">
                <div class="card-body mal-text">
                    <!-- ── Normal review mode: tick each field, answer questions, decide ── -->
                    <template v-if="!editRequestMode">
                        <div class="vo-head">
                            <ion-icon name="shield-checkmark-outline"></ion-icon>
                            <span>Verification</span>
                        </div>
                        <p class="vo-hint mal-text">ഓരോ വിവരവും പരിശോധിച്ച് "Verified" ടിക്ക് ചെയ്യുക. നിങ്ങളുടെ പുരോഗതി ഓട്ടോമാറ്റിക്കായി സേവ് ചെയ്യപ്പെടും.</p>

                        <!-- Verified applications are locked by default so a finalized decision
                             can't drift from a stray edit — this reopens it deliberately when the
                             officer made a mistake and needs to correct + resubmit. -->
                        <div class="vo-reedit-banner mal-text"
                            v-if="application.verification && application.verification.status === 'verified' && !reEditingVerified">
                            <ion-icon name="alert-circle-outline"></ion-icon>
                            <span>തെറ്റുണ്ടെങ്കിൽ തിരുത്തി വീണ്ടും സമർപ്പിക്കാം.</span>
                            <button type="button" class="vo-reedit-btn" @click="reEditingVerified = true">
                                <ion-icon name="create-outline"></ion-icon> Edit
                            </button>
                        </div>

                        <!-- Per-detail Verified ticks + optional note -->
                        <div class="vo-item-wrap" v-for="item in verifyItems" :key="item.key">
                            <div class="vo-item" :class="{ 'vo-item--ticked': verifyForm.ticks[item.key], 'vo-item--wrap': item.wrap }">
                                <div class="vo-item-info">
                                    <div class="vo-item-label">{{ item.label }}</div>
                                    <div class="vo-item-value">{{ item.value || '—' }}</div>
                                </div>
                                <div class="vo-item-actions">
                                    <button type="button" class="vo-note-btn"
                                        :class="{ 'vo-note-btn--on': noteOpen[item.key] || verifyForm.fieldNotes[item.key] }"
                                        @click="toggleNote(item.key)" title="Add note">
                                        <ion-icon name="create-outline"></ion-icon>
                                    </button>
                                    <label class="vo-tick" :class="{ 'vo-tick--on': verifyForm.ticks[item.key] }">
                                        <input type="checkbox" v-model="verifyForm.ticks[item.key]" hidden @change="scheduleAutosave">
                                        <ion-icon :name="verifyForm.ticks[item.key] ? 'checkmark-circle' : 'ellipse-outline'"></ion-icon>
                                        <span>Verified</span>
                                    </label>
                                </div>
                            </div>
                            <textarea v-if="noteOpen[item.key] || verifyForm.fieldNotes[item.key]"
                                v-model="verifyForm.fieldNotes[item.key]" @input="scheduleAutosave"
                                class="form-control form-control-sm vo-note-input" rows="2"
                                placeholder="ഈ വിവരത്തെക്കുറിച്ചുള്ള കുറിപ്പ് (optional)..."></textarea>
                        </div>

                        <!-- Extra questions defined in the portal -->
                        <template v-if="(application.verification_questions || []).length">
                            <div class="vo-subhead mal-text">അധിക ചോദ്യങ്ങൾ</div>
                            <template v-for="q in application.verification_questions" :key="q.key">
                                <label class="field-label mal-text">{{ q.label }} <span v-if="q.required" class="text-danger">*</span></label>
                                <select v-if="q.type === 'yes_no'" v-model="verifyForm.answers[q.key]" @change="scheduleAutosave" class="vo-answer-input mb-2">
                                    <option value="">Select</option>
                                    <option value="Yes">Yes</option>
                                    <option value="No">No</option>
                                </select>
                                <textarea v-else-if="q.type === 'textarea'" v-model="verifyForm.answers[q.key]" @input="scheduleAutosave" class="vo-answer-input mb-2" rows="2"></textarea>
                                <select v-else-if="q.type === 'options'" v-model="verifyForm.answers[q.key]" @change="scheduleAutosave" class="vo-answer-input mb-2">
                                    <option value="">Select</option>
                                    <option v-for="opt in (q.options || [])" :key="opt" :value="opt">{{ opt }}</option>
                                </select>
                                <input v-else v-model="verifyForm.answers[q.key]" @input="scheduleAutosave" type="text" class="vo-answer-input mb-2" />
                                <!-- Follow-up field for an "options" question when the selected option needs one -->
                                <input v-if="q.type === 'options' && (q.extra_field_options || []).includes(verifyForm.answers[q.key])"
                                    v-model="verifyForm.answers[q.key + '__extra']" @input="scheduleAutosave" type="text"
                                    class="vo-answer-input mb-2" :placeholder="q.extra_field_label || ''" />
                            </template>
                        </template>

                        <!-- Notes -->
                        <label class="field-label mal-text mt-2">Notes</label>
                        <textarea v-model="verifyForm.notes" @input="scheduleAutosave" class="vo-answer-input mb-3" rows="3"
                            placeholder="ആവശ്യമായ കുറിപ്പുകൾ ഇവിടെ എഴുതുക..."></textarea>

                        <!-- Actions: hidden once an officer has finalized Verified (nothing left to
                             decide) unless they've deliberately reopened it via the Edit button above. -->
                        <div class="vo-actions" v-if="!(application.verification && application.verification.status === 'verified') || reEditingVerified">
                            <button type="button" class="btn btn-outline-danger vo-act-btn"
                                :disabled="verify_saving" @click="saveVerification('not_verified')">Not Verified</button>
                            <button type="button" class="btn btn-outline-warning vo-act-btn"
                                :disabled="verify_saving" @click="startEditRequest">Request Edit</button>
                            <button type="button" class="btn btn-success vo-act-btn vo-act-btn--wide"
                                :disabled="verify_saving" @click="saveVerification('verified')">
                                <span v-if="verify_saving" class="spinner-border spinner-border-sm me-1"></span>
                                Verified
                            </button>
                            <button v-if="application.verification && application.verification.status === 'edit_requested'"
                                type="button" class="btn btn-outline-secondary vo-act-btn vo-act-btn--wide"
                                :disabled="verify_saving" @click="removeEditRequest">
                                <ion-icon name="close-circle-outline"></ion-icon> Remove Edit Request
                            </button>
                        </div>
                    </template>

                    <!-- ── Edit-request mode: pick exactly which fields the applicant may edit ── -->
                    <template v-else>
                        <div class="vo-head">
                            <ion-icon name="create-outline"></ion-icon>
                            <span>എഡിറ്റ് ചെയ്യേണ്ട വിവരങ്ങൾ തിരഞ്ഞെടുക്കുക</span>
                        </div>
                        <p class="vo-hint mal-text">തിരുത്തേണ്ട വിവരങ്ങൾ സെലക്ട് ചെയ്യുക. ബാക്കിയുള്ളവ അപേക്ഷകന് എഡിറ്റ് ചെയ്യാൻ കഴിയില്ല.</p>

                        <div class="vo-item-wrap" v-for="item in verifyItems" :key="item.key">
                            <div class="vo-item" :class="{ 'vo-item--ticked': editRequestSelections[item.key], 'vo-item--wrap': item.wrap }">
                                <div class="vo-item-info">
                                    <div class="vo-item-label">{{ item.label }}</div>
                                    <div class="vo-item-value">{{ item.value || '—' }}</div>
                                </div>
                                <div class="vo-item-actions">
                                    <button type="button" class="vo-note-btn"
                                        :class="{ 'vo-note-btn--on': editReqNoteOpen[item.key] || editRequestNotes[item.key] }"
                                        @click="editReqNoteOpen[item.key] = !editReqNoteOpen[item.key]" title="Add note">
                                        <ion-icon name="create-outline"></ion-icon>
                                    </button>
                                    <label class="vo-tick" :class="{ 'vo-tick--on': editRequestSelections[item.key] }">
                                        <input type="checkbox" v-model="editRequestSelections[item.key]" hidden>
                                        <ion-icon :name="editRequestSelections[item.key] ? 'checkmark-circle' : 'ellipse-outline'"></ion-icon>
                                        <span>എഡിറ്റ് ചെയ്യണം</span>
                                    </label>
                                </div>
                            </div>
                            <textarea v-if="editReqNoteOpen[item.key] || editRequestNotes[item.key]"
                                v-model="editRequestNotes[item.key]"
                                class="form-control form-control-sm vo-note-input" rows="2"
                                placeholder="എന്ത് തിരുത്തണം എന്ന് ഇവിടെ എഴുതുക..."></textarea>
                        </div>

                        <label class="field-label mal-text mt-2">Notes</label>
                        <textarea v-model="verifyForm.notes" class="vo-answer-input mb-3" rows="3"
                            placeholder="ആവശ്യമായ കുറിപ്പുകൾ ഇവിടെ എഴുതുക..."></textarea>

                        <div class="vo-actions">
                            <button type="button" class="btn btn-outline-secondary vo-act-btn"
                                :disabled="verify_saving" @click="cancelEditRequest">Cancel</button>
                            <button type="button" class="btn btn-warning vo-act-btn"
                                :disabled="verify_saving" @click="sendEditRequest">
                                <span v-if="verify_saving" class="spinner-border spinner-border-sm me-1"></span>
                                Send Edit Request
                            </button>
                        </div>
                    </template>
                </div>
            </div>
        </div>

        <!-- ============ Interview List (programme approvers only, once Verified) ============ -->
        <div class="section pt-0"
            v-if="application.is_approver && application.verification && application.verification.status === 'verified'">
            <div class="card">
                <div class="card-body mal-text">
                    <h6 class="text-primary mb-2">Interview List</h6>
                    <template v-if="application.interview_status">
                        <p class="mb-2 small text-muted">
                            <ion-icon name="checkmark-circle" style="color:#12a85a;"></ion-icon>
                            Interview List-ൽ ചേർത്തു
                            <span v-if="application.interview_moved_by_name"> · {{ application.interview_moved_by_name }}</span>
                            <span v-if="application.interview_moved_at"> · {{ application.interview_moved_at }}</span>
                        </p>
                        <button type="button" class="btn btn-outline-danger btn-sm" style="border-radius:8px;"
                            :disabled="interview_saving" @click="removeFromInterview">
                            Interview List-ൽ നിന്ന് നീക്കുക
                        </button>
                    </template>
                    <button v-else type="button" class="btn btn-success" style="border-radius:8px;width:100%;"
                        :disabled="interview_saving" @click="moveToInterview">
                        <span v-if="interview_saving" class="spinner-border spinner-border-sm me-1"></span>
                        Move To Interview List
                    </button>
                </div>
            </div>
        </div>
    </template>

    <div class="section pt-3" v-else-if="!loading">
        <div class="listed-detail mt-0">
            <h4 class="text-center mt-0 mal-text">അപേക്ഷ കണ്ടെത്തിയില്ല</h4>
        </div>
    </div>

    <alert-model :type="alert_type" :message="alert_message" ref="alert_model"></alert-model>
</template>

<script>
import UserService from "@/services/user.service";
import AlertModel from "../layouts/AlertModel.vue";

export default {
    name: "SnehaSparshamApplicationDetail",
    components: { AlertModel },
    data() {
        return {
            loading: true,
            application: {},
            alert_type: "",
            alert_message: "",
            verify_saving: false,
            verifyForm: { ticks: {}, fieldNotes: {}, answers: {}, notes: "" },
            noteOpen: {},
            autosaveTimer: null,
            // Whether a VO has deliberately reopened an already-Verified application to correct
            // a mistake and resubmit — reset to false on every load so it always starts locked.
            reEditingVerified: false,
            interview_saving: false,
            // Dedicated "select fields to edit" step, entered via the Request Edit button —
            // independent of the Verified ticks above (which drive Verified/Not Verified).
            editRequestMode: false,
            editRequestSelections: {},
            editRequestNotes: {},
            editReqNoteOpen: {},
            // Approver scoring — manual 0-10 marks per question, separate from the VO's own
            // auto-computed completion score (field.mark above).
            scores: {},
            comments: {},
            savingScores: false,
        };
    },
    computed: {
        editPath() {
            return (
                "/org/" + this.$route.params.sub_level + "/" + this.$route.params.id +
                "/sneha-sparsham-application/" + this.$route.params.application_id + "/edit"
            );
        },
        // Checklist items the VO ticks as Verified — family details + program fields.
        verifyItems() {
            const items = [];
            const r = this.application.relief || {};
            const push = (key, label, value, wrap) => items.push({ key, label, value, wrap: !!wrap });
            if (this.application.relief) {
                push("family_head_name", "കുടുംബ നാഥന്റെ പേര്", r.family_head_name);
                push("family_head_gender", "ലിംഗം", this.formatGender(r.family_head_gender));
                push("family_head_age", "വയസ്സ്", r.family_head_age);
                push("family_contact", "ഫോൺ", r.family_contact);
                push("family_place", "താമസിക്കുന്ന സ്ഥലം", r.family_place);
                push("family_reason_to_include", "ഉൾപ്പെടുത്താനുള്ള കാരണം", r.family_reason_to_include, true);
                push("zakath_eligible", "സകാത്ത് അർഹത", this.formatYesNo(r.zakath_eligible));
                push("family_members_count", "കുടുംബത്തിലെ ആകെ എണ്ണം", r.family_members_count);
                push("orphan_count", "യാതീം എണ്ണം", r.orphan_count);
                push("widow_count", "വിധവ എണ്ണം", r.widow_count);
            }
            (this.application.extra_fields || []).forEach((f) => push("extra_" + f.key, f.label, f.value));
            return items;
        },
        hasInvalidScores() {
            return Object.values(this.scores).some((v) => this.scoreInvalid(v));
        },
    },
    mounted() {
        if (!this.$store.state.auth.user) {
            location.replace("/");
            return;
        }
        this.$root.updateParent("അപേക്ഷ വിവരങ്ങൾ");
        this.loadApplication();
    },
    methods: {
        statusLabel(s) {
            return s === 'verified' ? 'Verified'
                : s === 'not_verified' ? 'Not Verified'
                : s === 'edit_requested' ? 'Edit Requested'
                : s === 'resubmitted' ? 'Resubmitted' : s;
        },
        statusColor(s) {
            return s === 'verified' ? '#12a85a' : s === 'resubmitted' ? '#1a418c' : '#d9534f';
        },
        // Mark awarded for a single verification question's answer, per the portal's marks
        // config on that question — null when no mark is configured for this question/answer,
        // in which case the badge is simply hidden.
        answerMark(q) {
            const answers = (this.application.verification && this.application.verification.extra_answers) || {};
            if (q.type === 'options' || q.type === 'yes_no') {
                const marks = q.marks || {};
                const answer = answers[q.key];
                if (answer !== undefined && answer !== null && Object.prototype.hasOwnProperty.call(marks, answer)) {
                    return marks[answer];
                }
                return null;
            }
            if (q.mark === undefined || q.mark === null || q.mark === '') {
                return null;
            }
            const val = (answers[q.key] || "").toString().trim();
            return val !== '' ? q.mark : 0;
        },
        toggleNote(key) {
            this.noteOpen[key] = !this.noteOpen[key];
        },
        saveVerification(status) {
            // Required extra-questions are only a condition of approving ("verified") — a VO
            // must still be able to flag a problem (Not Verified) without first answering every
            // required question. ("Request Edit" no longer goes through this method at all — see
            // startEditRequest()/sendEditRequest() for the dedicated field-selection flow.)
            if (status === "verified") {
                for (const q of this.application.verification_questions || []) {
                    if (q.required && !(this.verifyForm.answers[q.key] || "").toString().trim()) {
                        this.alert_type = "Warning";
                        this.alert_message = q.label + " is required.";
                        this.$refs.alert_model.openModel();
                        return;
                    }
                }
            }
            clearTimeout(this.autosaveTimer);
            this.verify_saving = true;
            UserService.authPostRequest("store-sneha-sparsham-verification", {
                application_id: parseInt(this.$route.params.application_id, 10),
                status: status,
                notes: this.verifyForm.notes,
                field_ticks: this.verifyForm.ticks,
                field_notes: this.verifyForm.fieldNotes,
                extra_answers: this.verifyForm.answers,
            }).then(
                (response) => {
                    this.verify_saving = false;
                    this.alert_type = response.data.status === "success" ? "Success" : "Warning";
                    this.alert_message = response.data.message;
                    this.$refs.alert_model.openModel();
                    if (response.data.status === "success") this.loadApplication();
                },
                () => { this.verify_saving = false; }
            );
        },
        // Debounced background draft-save as the VO ticks/types, so leaving the page without
        // pressing Verified/Not Verified doesn't lose their progress — see loadApplication()'s
        // 'in_progress' hydration below for the other half of this.
        scheduleAutosave() {
            clearTimeout(this.autosaveTimer);
            this.autosaveTimer = setTimeout(() => this.autosaveVerification(), 800);
        },
        autosaveVerification() {
            if (this.editRequestMode || !this.application.is_vo) return;
            UserService.authPostRequest("store-sneha-sparsham-verification", {
                application_id: parseInt(this.$route.params.application_id, 10),
                autosave: true,
                notes: this.verifyForm.notes,
                field_ticks: this.verifyForm.ticks,
                field_notes: this.verifyForm.fieldNotes,
                extra_answers: this.verifyForm.answers,
            }).catch(() => {});
        },
        // Enter the dedicated "select fields to edit" step. Always starts from a clean slate —
        // independent of the Verified ticks — so the VO makes an explicit choice each time.
        startEditRequest() {
            clearTimeout(this.autosaveTimer);
            this.editRequestSelections = {};
            this.editRequestNotes = {};
            this.editReqNoteOpen = {};
            this.editRequestMode = true;
        },
        cancelEditRequest() {
            this.editRequestMode = false;
        },
        sendEditRequest() {
            const hasSelection = Object.values(this.editRequestSelections).some(Boolean);
            if (!hasSelection) {
                this.alert_type = "Warning";
                this.alert_message = "എഡിറ്റ് ചെയ്യേണ്ട ഒരു വിവരമെങ്കിലും തിരഞ്ഞെടുക്കുക.";
                this.$refs.alert_model.openModel();
                return;
            }
            // Selected = needs edit (unlocked for the applicant); unselected = confirmed correct
            // (locked). This reuses the same field_ticks/field_notes columns the edit form reads,
            // just built from this dedicated selection instead of the Verified-tick list.
            const ticks = {};
            const notes = {};
            (this.verifyItems || []).forEach((item) => {
                ticks[item.key] = !this.editRequestSelections[item.key];
                if (this.editRequestSelections[item.key] && this.editRequestNotes[item.key]) {
                    notes[item.key] = this.editRequestNotes[item.key];
                }
            });
            clearTimeout(this.autosaveTimer);
            this.verify_saving = true;
            UserService.authPostRequest("store-sneha-sparsham-verification", {
                application_id: parseInt(this.$route.params.application_id, 10),
                status: "edit_requested",
                notes: this.verifyForm.notes,
                field_ticks: ticks,
                field_notes: notes,
                extra_answers: this.verifyForm.answers,
            }).then(
                (response) => {
                    this.verify_saving = false;
                    this.alert_type = response.data.status === "success" ? "Success" : "Warning";
                    this.alert_message = response.data.message;
                    this.$refs.alert_model.openModel();
                    if (response.data.status === "success") {
                        this.editRequestMode = false;
                        this.loadApplication();
                    }
                },
                () => { this.verify_saving = false; }
            );
        },
        // Retract an already-sent "Request Edit" — resets the status to pending (keeping the
        // VO's ticks/notes/answers intact so they can adjust and resubmit) so it stops showing
        // as an outstanding edit request to the applicant.
        removeEditRequest() {
            if (!confirm("എഡിറ്റ് അഭ്യർത്ഥന പിൻവലിക്കണോ?")) {
                return;
            }
            this.verify_saving = true;
            UserService.authPostRequest("remove-sneha-sparsham-edit-request", {
                application_id: parseInt(this.$route.params.application_id, 10),
            }).then(
                (response) => {
                    this.verify_saving = false;
                    this.alert_type = response.data.status === "success" ? "Success" : "Warning";
                    this.alert_message = response.data.message;
                    this.$refs.alert_model.openModel();
                    if (response.data.status === "success") this.loadApplication();
                },
                () => { this.verify_saving = false; }
            );
        },
        moveToInterview() {
            this.interview_saving = true;
            UserService.authPostRequest("move-sneha-sparsham-application-to-interview", {
                application_id: parseInt(this.$route.params.application_id, 10),
            }).then(
                (response) => {
                    this.interview_saving = false;
                    this.alert_type = response.data.status === "success" ? "Success" : "Warning";
                    this.alert_message = response.data.message;
                    this.$refs.alert_model.openModel();
                    if (response.data.status === "success") this.loadApplication();
                },
                () => { this.interview_saving = false; }
            );
        },
        removeFromInterview() {
            if (!confirm("Interview List-ൽ നിന്ന് നീക്കണോ?")) {
                return;
            }
            this.interview_saving = true;
            UserService.authPostRequest("remove-sneha-sparsham-application-from-interview", {
                application_id: parseInt(this.$route.params.application_id, 10),
            }).then(
                (response) => {
                    this.interview_saving = false;
                    this.alert_type = response.data.status === "success" ? "Success" : "Warning";
                    this.alert_message = response.data.message;
                    this.$refs.alert_model.openModel();
                    if (response.data.status === "success") this.loadApplication();
                },
                () => { this.interview_saving = false; }
            );
        },
        loadApplication() {
            this.loading = true;
            this.reEditingVerified = false;
            UserService.authPostRequest("get-sneha-sparsham-application", {
                application_id: parseInt(this.$route.params.application_id, 10),
            }).then((response) => {
                this.loading = false;
                if (response.data.status === "success") {
                    this.application = response.data.data || {};
                    const v = this.application.verification;
                    // field_ticks/field_notes are reused as storage for the edit-request's
                    // "which fields did the applicant get to unlock" selection too (see
                    // sendEditRequest()). Only treat them as genuine Verified-tick state when
                    // they were actually written by the normal Verified/Not-Verified flow (or its
                    // 'in_progress' autosaved draft) — otherwise every field the VO didn't select
                    // for editing would wrongly show up here as "Verified" the moment the
                    // edit-request screen reloads.
                    const ticksAreFromNormalFlow = v && (v.status === "verified" || v.status === "not_verified" || v.status === "in_progress");
                    // PHP serializes an EMPTY associative store as a JSON array ([]), so these can
                    // arrive as arrays. They must be plain objects here: v-model writes string keys
                    // (field keys), and JSON.stringify silently drops string keys set on an array —
                    // which was making every autosaved tick come back empty. Coerce to {}.
                    const toObj = (x) => (x && typeof x === "object" && !Array.isArray(x)) ? x : {};
                    this.verifyForm = {
                        ticks: ticksAreFromNormalFlow ? toObj(v.field_ticks) : {},
                        fieldNotes: ticksAreFromNormalFlow ? toObj(v.field_notes) : {},
                        answers: toObj(v && v.extra_answers),
                        notes: (v && v.notes) || "",
                    };
                    this.scores = { ...toObj(this.application.approver_scores) };
                    this.comments = { ...toObj(this.application.approver_comments) };
                } else {
                    this.alert_type = "Warning";
                    this.alert_message = response.data.message || "അപേക്ഷ ലോഡ് ചെയ്യാൻ സാധിച്ചില്ല.";
                    this.$refs.alert_model.openModel();
                }
            }).catch(() => {
                this.loading = false;
                this.alert_type = "Warning";
                this.alert_message = "അപേക്ഷ ലോഡ് ചെയ്യാൻ സാധിച്ചില്ല.";
                this.$refs.alert_model.openModel();
            });
        },
        formatGender(value) {
            if (value === "Male") return "പുരുഷൻ";
            if (value === "Female") return "സ്ത്രീ";
            return value || "—";
        },
        formatYesNo(value) {
            if (value === "Yes" || value === "Y") return "അതെ";
            if (value === "No" || value === "N") return "ഇല്ല";
            return value || "—";
        },
        // verifyItems() prefixes programme-question keys with "extra_" (to keep them
        // namespaced apart from family-detail keys for the VO's own ticks/notes) — the score
        // record uses the plain, unprefixed key (matching the backend's program.form_fields
        // keys), so this strips it back off before reading/writing `scores`.
        scoreFieldKey(itemKey) {
            return itemKey.indexOf("extra_") === 0 ? itemKey.slice(6) : itemKey;
        },
        isScorableItem(itemKey) {
            return (this.application.approver_scorable_keys || []).includes(this.scoreFieldKey(itemKey));
        },
        isCommentableItem(itemKey) {
            return (this.application.approver_commentable_keys || []).includes(this.scoreFieldKey(itemKey));
        },
        // Blank/unset is fine (not yet scored) — only an actual out-of-range number is an error.
        scoreInvalid(value) {
            if (value === null || value === undefined || value === "") return false;
            const n = Number(value);
            return Number.isNaN(n) || n < 0 || n > 10;
        },
        saveScores() {
            if (this.savingScores || this.hasInvalidScores) return;
            this.savingScores = true;
            UserService.authPostRequest("store-sneha-sparsham-application-score", {
                application_id: this.application.id,
                scores: this.scores,
                comments: this.comments,
            }).then((response) => {
                this.savingScores = false;
                if (response.data.status === "success") {
                    const d = response.data.data || {};
                    this.scores = { ...(d.scores || {}) };
                    this.comments = { ...(d.comments || {}) };
                    this.application.approver_score_total = d.total_score;
                    this.application.approver_score_max = d.max_score;
                    this.alert_type = "Success";
                    this.alert_message = response.data.message || "സ്കോർ സേവ് ചെയ്തു.";
                    this.$refs.alert_model.openModel();
                } else {
                    this.alert_type = "Warning";
                    this.alert_message = response.data.message || "സ്കോർ സേവ് ചെയ്യാൻ സാധിച്ചില്ല.";
                    this.$refs.alert_model.openModel();
                }
            }).catch(() => {
                this.savingScores = false;
                this.alert_type = "Warning";
                this.alert_message = "സ്കോർ സേവ് ചെയ്യാൻ സാധിച്ചില്ല.";
                this.$refs.alert_model.openModel();
            });
        },
    },
};
</script>

<style scoped>
.sneha-header-card .card-body { padding: 18px 16px 16px; }
.sneha-header-title {
    font-size: 1.02rem;
    font-weight: 800;
    color: #1a418c;
    margin: 0 0 10px;
    line-height: 1.35;
}
.sneha-app-number {
    display: inline-block;
    font-size: 0.78rem;
    font-weight: 700;
    letter-spacing: 0.3px;
    color: #1a418c;
    background: #f4f7ff;
    border: 1px solid #dde4f5;
    border-radius: 6px;
    padding: 2px 8px;
}
.sneha-approver-score-badge {
    display: inline-block;
    font-size: 0.76rem;
    font-weight: 700;
    letter-spacing: 0.2px;
    color: #a15c00;
    background: #fff8ed;
    border: 1px solid #fde8b0;
    border-radius: 6px;
    padding: 2px 8px;
    margin-left: 6px;
}
.sneha-header-highlight {
    margin: 12px 0 0;
    padding: 10px 12px;
    background: #f9fbfd;
    border: 1px solid #eef0f5;
    border-radius: 10px;
}
.sneha-score-input {
    display: flex;
    align-items: center;
    gap: 6px;
    margin-top: 6px;
    padding-top: 6px;
    border-top: 1px dashed #eef0f5;
}
.sneha-score-label {
    font-size: 0.74rem;
    font-weight: 700;
    color: #a15c00;
}
.sneha-score-field {
    width: 48px;
    padding: 3px 6px;
    font-size: 0.85rem;
    font-weight: 700;
    text-align: center;
    color: #27173E;
    border: 1px solid #dde4f5;
    border-radius: 6px;
}
.sneha-score-field--error {
    border-color: #d9534f;
    background: #fff5f5;
    color: #d9534f;
}
.sneha-score-error {
    font-size: 0.72rem;
    font-weight: 700;
    color: #d9534f;
}
.sneha-score-max {
    font-size: 0.74rem;
    color: #9aa4b2;
}
.sneha-comment-input {
    margin-top: 4px;
}
.sneha-comment-field {
    width: 100%;
    padding: 5px 8px;
    font-size: 0.8rem;
    color: #27173E;
    border: 1px solid #dde4f5;
    border-radius: 6px;
}
.sneha-save-score-btn {
    display: block;
    width: 100%;
    margin-top: 12px;
    border-radius: 10px;
}
.sneha-top-field-label {
    display: block;
    font-size: 0.72rem;
    text-transform: uppercase;
    letter-spacing: 0.3px;
    color: #6c757d;
    margin-bottom: 3px;
}
.sneha-top-field-value {
    font-size: 0.92rem;
    font-weight: 700;
    color: #27173E;
}
.sneha-header-date {
    margin: 10px 0 0;
    font-size: 0.76rem;
    color: #9aa4b2;
}
.card {
    border: 1px solid #eef0f5;
    border-radius: 14px;
    box-shadow: 0 1px 4px rgba(0, 0, 0, 0.05);
}
.card-body { padding: 14px 16px; }
.card h6 {
    font-size: 13px;
    font-weight: 800;
    color: #1a418c;
    margin-bottom: 10px;
    padding-bottom: 8px;
    border-bottom: 1px solid #f1f3f7;
}

.detail-row {
    display: flex;
    justify-content: space-between;
    gap: 12px;
    padding: 9px 0;
    border-bottom: 1px solid #f4f5f7;
    font-size: 1rem;
    line-height: 1.4;
}
.detail-row:last-child {
    border-bottom: none;
    padding-bottom: 0;
}
.detail-label {
    color: #8a94a6;
    flex: 0 0 44%;
    font-size: 0.92rem;
}
.detail-value {
    color: #1e293b;
    font-weight: 600;
    text-align: right;
    flex: 1;
    word-break: break-word;
}
/* Long free-text values (e.g. reason-to-include) read better stacked below the label
   and left-aligned than squeezed into a right-aligned column next to it. */
.detail-row--stacked {
    flex-direction: column;
    align-items: flex-start;
    gap: 4px;
}
.detail-row--stacked .detail-label {
    flex: none;
}
.detail-row--stacked .detail-value {
    text-align: left;
    width: 100%;
}
.child-chip {
    background: #f8f9fa;
    border-radius: 8px;
    padding: 8px 10px;
    margin-bottom: 6px;
    font-size: 0.9rem;
}
.field-label {
    font-size: 15px;
    font-weight: 600;
    color: #64748b;
    margin-bottom: 4px;
    display: block;
}

/* Explicit styling (not reliant on Bootstrap form-control/form-select being loaded) so answer
   fields for the portal-defined questions are clearly recognizable as inputs. */
.vo-answer-input {
    display: block;
    width: 100%;
    border: 1.5px solid #cbd5e1;
    border-radius: 10px;
    padding: 10px 12px;
    font-size: 17px;
    color: #1e293b;
    background: #fff;
}
.vo-answer-input:focus {
    outline: none;
    border-color: #1a418c;
}

/* ── Verification card ───────────────────────────── */
.vo-card { border: 1.5px solid #dbe3ff !important; background: #fbfcff; }
.vo-head {
    display: flex;
    align-items: center;
    gap: 7px;
    font-size: 16px;
    font-weight: 800;
    color: #1a418c;
}
.vo-head ion-icon { font-size: 20px; }
.vo-hint { font-size: 13px; color: #8a94a6; margin: 3px 0 12px; }
.vo-reedit-banner {
    display: flex;
    align-items: center;
    gap: 8px;
    background: #fff8e6;
    border: 1px solid #f5deab;
    border-radius: 10px;
    padding: 9px 12px;
    margin-bottom: 12px;
    font-size: 13px;
    color: #8a6d1f;
}
.vo-reedit-banner ion-icon { font-size: 17px; flex-shrink: 0; }
.vo-reedit-banner span { flex: 1; min-width: 0; line-height: 1.35; }
.vo-reedit-btn {
    display: inline-flex;
    align-items: center;
    gap: 4px;
    flex-shrink: 0;
    border: none;
    border-radius: 8px;
    padding: 6px 10px;
    background: #1a418c;
    color: #fff;
    font-size: 12px;
    font-weight: 700;
}
.vo-reedit-btn ion-icon { font-size: 14px; }
.vo-subhead {
    font-size: 14px;
    font-weight: 800;
    color: #1a418c;
    margin: 16px 0 8px;
    padding-top: 12px;
    border-top: 1px solid #eef0f5;
}
.vo-item {
    display: flex;
    align-items: center;
    justify-content: space-between;
    gap: 10px;
    background: #fff;
    border: 1px solid #eef0f5;
    border-radius: 10px;
    padding: 9px 12px;
    margin-bottom: 7px;
    transition: border-color 0.15s ease, background 0.15s ease;
}
.vo-item--ticked {
    border-color: #b7f0d4;
    background: #f4fdf8;
}
/* Long free-text values (e.g. reason-to-include) wrap to several lines — stack the
   actions (note/verified) below the value instead of vertically centering them
   against a multi-line block. */
.vo-item--wrap {
    flex-direction: column;
    align-items: stretch;
}
.vo-item--wrap .vo-item-actions {
    justify-content: flex-end;
    margin-top: 8px;
}
.vo-item-info { min-width: 0; flex: 1; }
.vo-item-label { font-size: 13px; color: #8a94a6; line-height: 1.3; }
.vo-item-value { font-size: 16px; font-weight: 700; color: #1e293b; line-height: 1.35; word-break: break-word; margin-top: 2px; }
.vo-tick {
    display: inline-flex;
    align-items: center;
    gap: 4px;
    flex-shrink: 0;
    font-size: 13px;
    font-weight: 700;
    color: #94a3b8;
    cursor: pointer;
    margin: 0;
    user-select: none;
}
.vo-tick ion-icon { font-size: 20px; }
.vo-tick--on { color: #12a85a; }

.vo-item-wrap { margin-bottom: 7px; }
.vo-item-wrap .vo-item { margin-bottom: 0; }
.vo-item-actions { display: flex; align-items: center; gap: 8px; flex-shrink: 0; }
.vo-note-btn {
    display: inline-flex;
    align-items: center;
    justify-content: center;
    width: 28px;
    height: 28px;
    border-radius: 8px;
    border: 1px solid #e2e8f0;
    background: #fff;
    color: #94a3b8;
    cursor: pointer;
    flex-shrink: 0;
}
.vo-note-btn ion-icon { font-size: 15px; }
.vo-note-btn--on { border-color: #1a418c; color: #1a418c; background: #eef2ff; }
.vo-note-input {
    border-radius: 0 0 10px 10px;
    border-top: none;
    background: #fbfcff;
    margin-top: -1px;
}
.vo-note-readonly {
    font-size: 13px;
    color: #64748b;
    background: #fbfcff;
    border: 1px solid #eef0f5;
    border-top: none;
    border-radius: 0 0 10px 10px;
    padding: 8px 12px;
    margin: -7px 0 7px;
}
.sneha-q-mark-badge {
    background: #1a418c;
    display: inline-flex;
    align-items: center;
    gap: 3px;
    margin-top: 2px;
}
.sneha-q-mark-badge ion-icon {
    font-size: 13px;
}

.vo-actions {
    display: grid;
    grid-template-columns: 1fr 1fr;
    gap: 8px;
    margin-top: 6px;
}
.vo-act-btn {
    border-radius: 10px;
    font-size: 14px;
    font-weight: 700;
    padding: 9px 6px;
}
.vo-act-btn--wide {
    grid-column: 1 / -1;
}
</style>
